import { useEffect } from 'react';
import { connect } from 'react-redux';
import * as selector from './accountsSelector';
import { AwaitingFundsView } from './awaitingFundsView/AwaitingFundsView';
import styles from './Dashboard.module.css';
import OverdueView from './OverdueView/OverdueView';
import OutstandingView from './OutstandingView/OutstandingView';
import EndedView from './EndedView/EndedView';
import PendingView from './pendingView/PendingView';
import UnknownView from './UnknownView/UnknownView';
import ApplyFlexiLoanInfoView from '../applyFlexiLoanView/ApplyFlexiLoanInfoView/ApplyFlexiLoanInfoView';
import { SpillBanner } from '../spill/SpillBanner';
import { RootState } from '../../iwProvider/configureStore';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { Dispatch } from 'redux';
import NewOutstandingView from './NewOutstandingView/NewOutstandingView';
import { TProductType } from '../dashboard/Dashboard.types';
import { Card } from '@iwoca/orion';
import messages from './Dashboard.messages';
import { FormattedMessage } from 'react-intl';
import NewOverdueView from './NewOverdueView/NewOverdueView';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { getCookie } from '@api/HelperAPI';
import { LoadingAnimation } from '../ui/loadingAnimation/LoadingAnimation';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import { getSplitTestQueryConfig } from '@api/lendingAPI/queryConfigs';

const mapStateToProps = (state: RootState) => {
    const productType: TProductType = selector.getProductTypeFromURL();

    return {
        ready: state.multiLoanDashboard.ready,
        status: selector.getDashboardStatus(
            state,
            selector.getProductTypeFromURL()
        ),
        productType,
        accountId: selector.getAccountId(state, productType)
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    redirectToMyOffers: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/my-offers/' }
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const MultiLoanDashboard = ({
    ready,
    status,
    redirectToMyOffers,
    productType,
    accountId
}: PropsFromRedux) => {
    useEffect(() => {
        if (!ready) return;

        if (
            status === selector.DECLINED_STATUS ||
            status === selector.DEFERRED_STATUS
        ) {
            redirectToMyOffers();
        }
    }, [ready, status, redirectToMyOffers]);

    if (!ready) {
        return <MainDashboardLoading />;
    }

    return (
        <div data-testid="multi-loan-dashboard" className={styles.Dashboard}>
            <DashboardView
                status={status}
                accountId={accountId}
                productType={productType}
            />
        </div>
    );
};

const DashboardView = ({
    status,
    accountId,
    productType
}: {
    status: string;
    accountId: string;
    productType: TProductType;
}) => {
    const statekey = useStateKey();
    //split test is currently only available in staging
    const isDE = getCountry() === 'DE';

    const { isLoading, data: branch } = useQuery({
        ...getSplitTestQueryConfig(statekey, 'de_new_dashboard_test_2024_q3'),
        select: (data) => data.data.branch,
        enabled: isDE
    });

    useEffect(() => {
        if (branch) {
            tracking.ampli.identify(statekey, {
                //@ts-expect-error: missing types
                'Split test DE new dashboard test 2024 Q3': branch
            });
        }
    }, [branch, statekey]);

    // The old dashboard is now owned by DE. The cookie check is necessary to keep the
    // associated tests, which are mostly written with English assertions
    const showNewDashboard =
        (isDE && branch === 'INCLUDE') ||
        (getCountry() === 'UK' && getCookie('showOldDashboard') !== 'true');

    if (isDE && isLoading)
        return (
            <MainDashboardLoading data-testid="multi-loan-dashboard-loading" />
        );

    switch (status) {
        case selector.OUTSTANDING_STATUS:
            return (
                <>
                    <SpillBanner />

                    {showNewDashboard ? (
                        <NewOutstandingView
                            accountId={accountId}
                            productType={productType}
                        />
                    ) : (
                        <OutstandingView
                            accountId={accountId}
                            productType={productType}
                        />
                    )}
                </>
            );

        case selector.OVERDUE_STATUS:
            return (
                <>
                    <SpillBanner />
                    {showNewDashboard ? (
                        <NewOverdueView
                            accountId={accountId}
                            productType={productType}
                        />
                    ) : (
                        <OverdueView
                            accountId={accountId}
                            productType={productType}
                        />
                    )}
                </>
            );
        case selector.ENDED_STATUS:
            return (
                <>
                    <SpillBanner />
                    <EndedView
                        accountId={accountId}
                        productType={productType}
                    />
                </>
            );
        case selector.APPLY_FLEXI_LOAN_STATUS:
            return <ApplyFlexiLoanInfoView />;
        case selector.PENDING_FLEXI_LOAN_STATUS:
            return <PendingView />;

        case selector.AWAITING_FUNDS_STATUS:
        case selector.TOPUP_STATUS:
            return <AwaitingFundsView />;

        case selector.UNKNOWN_STATUS:
        default:
            return (
                <UnknownView
                    accountId={accountId}
                    productType={productType}
                    accountStatus={status}
                />
            );
    }
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(MultiLoanDashboard);

const MainDashboardLoading = () => {
    return (
        <div className={styles.mainDashboardLoadingContainer}>
            <Card className={styles.mainDashboardLoadingCard}>
                <LoadingAnimation data-testid="loading-spinner" />
                <p className={styles.dashboardLoadingMessage}>
                    <FormattedMessage {...messages.loadingText} />
                </p>
            </Card>
        </div>
    );
};
